import { type FC, type ReactNode, Fragment, useEffect } from "react"
import { Dialog, Disclosure, Popover } from "@headlessui/react"
import Logo from "~/icons/Logo"
import Close from "~/icons/Close"
import ChevronDown from "~/icons/ChevronDown"
import classNames from "classnames"
import Link from "~/components/Link"
import { ANALYTICS_EVENTS } from "~/helpers/analyticsEvents"
import type {
  WithContentfulHeaderLocationFragment,
  WithExperienceFragment,
  WithLinkFragment
} from "~/contentful"
import { contentfulImageUrl } from "~/helpers/contentful"
import ResponsiveContentfulImage from "../ResponsiveContentfulImage"
import { useAnalytics } from "~/providers/AnalyticsProvider"
import ArrowRight from "~/icons/ArrowRight"
import HappyFace from "~/icons/HappyFace"
import Confetti from "~/icons/Confetti"
import Search from "~/icons/Search"
import QuestionMark from "~/icons/QuestionMark"
import LocationSelectPopover from "../design-system/organisms/LocationSelectPopover"
import { useFetcher, useNavigate, useNavigation } from "@remix-run/react"
import {
  getLocationCardHeadline,
  getLocationCardSubline,
  resolveLocationRouteSlug
} from "~/helpers/location"
import { useLocationSelectNavigation } from "~/hooks/useLocationSelectNavigation"
import type { loader as headerActivityLoader } from "~/routes/api.header-activity.$id"
import { useConfigContext } from "~/providers/ConfigProvider"
import { Button } from "../design-system/atoms"
import ThingsToDo from "~/icons/ThingsToDo"
import Location2 from "~/icons/Location2"
import Spinner from "~/icons/Spinner"

const HeaderMobileMenuSection: FC<{
  title: string
  icon?: ReactNode
  description?: string
  defaultOpen?: boolean
  noOpenedBorder?: boolean
  children?: ReactNode
}> = ({
  title,
  icon,
  description,
  defaultOpen,
  noOpenedBorder = false,
  children
}) => {
  const { track } = useAnalytics()
  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <>
          <Disclosure.Button
            className={classNames(
              "flex w-full items-center border-white text-left ",
              {
                "border-b": !noOpenedBorder || !open,
                "border-none border-opacity-0": noOpenedBorder && open,
                "border-cream-100 bg-white px-6 pt-6 pb-4 text-camp-primary text-opacity-70":
                  open,
                "border-opacity-10 text-white p-6": !open
              }
            )}
            onClick={() => {
              track(ANALYTICS_EVENTS.BUTTON_CLICKED, {
                text: title,
                type: "Mobile Navigation Section",
                path: window.location.pathname
              })
            }}
          >
            <div className="mr-4 flex flex-1 flex-col gap-2">
              <div className="flex space-x-3 fill-current stroke-none">
                {icon ?? null}
                <h2 className="text-2xl">{title}</h2>
              </div>
              {description != null && (
                <p className="text-sm text-opacity-75">{description}</p>
              )}
            </div>
            <ArrowRight
              className={classNames("h-4 w-4 stroke-4 transition-all", {
                "text-white": !open,
                "text-camp-primary text-opacity-70 rotate-90 transform": open
              })}
            />
          </Disclosure.Button>
          <Disclosure.Panel
            className="bg-white text-xl"
            style={{ borderStyle: "none" }}
          >
            {children}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  )
}

const HeaderMobileMenuLocations: FC<{
  locationPages: WithContentfulHeaderLocationFragment[]
}> = ({ locationPages }) => {
  return (
    <div className="bg-white border-2 border-white">
      <ul>
        {locationPages.map(locationPage => {
          const headline = getLocationCardHeadline(locationPage.name)
          const subline = getLocationCardSubline(
            locationPage.name,
            locationPage.city
          )

          return (
            <li key={locationPage.sys.id}>
              <Link
                to={
                  locationPage.slug
                    ? `/${resolveLocationRouteSlug(locationPage.slug)}`
                    : null
                }
                className="flex px-6 py-4"
              >
                <div className="mr-5 w-16">
                  {locationPage.logo?.url && (
                    <img
                      loading="lazy"
                      src={contentfulImageUrl(locationPage.logo.url, {
                        width: 280,
                        height: 200
                      })}
                      className="h-full w-full object-contain"
                      alt=""
                    />
                  )}
                </div>
                <span className="block flex-1">
                  <h2 className="font-serif">{headline}</h2>

                  {locationPage.slug && subline && (
                    <p
                      className={classNames("text-base opacity-60", {
                        uppercase: subline.uppercase
                      })}
                    >
                      {subline.text}
                    </p>
                  )}
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const HeaderMobileMenuItem: FC<{
  title: string
  icon?: ReactNode
  ariaLabel?: string
  url: string
  subitem?: boolean
  variant?: "primary" | "secondary"
}> = ({ title, icon, ariaLabel, url, subitem, variant = "primary" }) => (
  <div className="border-b border-white border-opacity-10">
    <Link
      to={url}
      aria-label={ariaLabel}
      className="flex items-center justify-between p-6"
    >
      <div className="flex space-x-3">
        {icon ? icon : null}
        <span
          className={classNames(
            "flex-1 text-2xl ml-4",
            subitem
              ? variant == "primary"
                ? "text-camp-primary"
                : "text-red-500"
              : "text-white"
          )}
        >
          {title}
        </span>
      </div>
      <ArrowRight className={"h-4 w-4 stroke-4 text-white"} />
    </Link>
  </div>
)

const HeaderMobileMenuColumn: FC<{
  title?: string
  links: {
    title: string
    url: string
  }[]
}> = ({ title, links }) => (
  <div className="">
    {title ? <h2 className="mb-4 font-serif text-white">{title}</h2> : null}
    <ul className="space-y-4">
      {links.map(link => (
        <li
          key={link.url}
          className="text-lg text-white text-opacity-75 transition-colors duration-200 hover:text-white"
        >
          <Link to={link.url} className="" aria-label="About Camp">
            {link.title}
          </Link>
        </li>
      ))}
    </ul>
  </div>
)

const HeaderMobileMenuV2: FC<{
  mobileMenuState: "open" | "closed" | "locations"
  locationPages: WithContentfulHeaderLocationFragment[]
  experiences: WithExperienceFragment[]
  eventLinks: WithLinkFragment[]
  activityLinks: WithLinkFragment[]
  onClose: () => void
  showSearch: boolean
  customBackgroundColor?: string
  locationSlug?: string | null
  shopPromoLinks?: WithLinkFragment[]
}> = ({
  mobileMenuState,
  locationPages,
  activityLinks,
  onClose,
  customBackgroundColor,
  locationSlug,
  shopPromoLinks
}) => {
  const navigate = useNavigate()
  const navigation = useNavigation()
  const {
    effectiveLocationName,
    getLocationSelectDestination,
    cmsLocationSlug,
    publicLocationSiteSlug
  } = useLocationSelectNavigation(locationPages, locationSlug)

  const selectedLocation = locationPages.find(
    loc => loc.name === effectiveLocationName
  )

  const config = useConfigContext()

  const activityTypeId = (config.stubActivityTypeId ??
    selectedLocation?.activityType?.activityTypeId) as string | undefined

  const activityFetcher = useFetcher<typeof headerActivityLoader>()

  const featuredActivity = activityFetcher.data?.activityType

  const isRedirecting = navigation.state === "loading"

  // Load featured activity when menu opens and we have an activityTypeId
  useEffect(() => {
    if (
      mobileMenuState !== "closed" &&
      activityTypeId &&
      activityFetcher.state === "idle" &&
      !activityFetcher.data
    ) {
      activityFetcher.load(`/api/header-activity/${activityTypeId}`)
    }
  }, [mobileMenuState, activityTypeId, activityFetcher])

  const shortName = getLocationCardHeadline(effectiveLocationName)
  const locationSelectButtonShortName =
    shortName === "New York City" ? "NYC" : shortName
  const displayLocationName = `CAMP ${locationSelectButtonShortName}`

  const featuredExperiencePath =
    selectedLocation && selectedLocation.slug && activityTypeId
      ? `/${resolveLocationRouteSlug(selectedLocation.slug)}/activities/${activityTypeId}`
      : null

  return (
    <Dialog
      open={mobileMenuState !== "closed"}
      onClose={onClose}
      as="div"
      className="lg:hidden"
      aria-hidden="true"
    >
      <div className="fixed inset-0 z-60 bg-white" aria-hidden />

      <Dialog.Panel
        className="fixed left-0 top-0 z-70 h-full w-full flex flex-col bg-[#365eae] bg-[url('/images/mobile-menu-v3-vector.svg')] bg-contain bg-[center_bottom_0px] bg-no-repeat shadow-[30px_14px_20px_rgba(0,0,0,0.1)] sm:w-10/12 sm:max-w-sm"
        style={{
          backgroundColor: customBackgroundColor
        }}
      >
        <header className="flex h-[60px] shrink-0 items-center gap-6 bg-camp-primary p-6 justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" title="Home">
              {selectedLocation?.logo?.url ? (
                <ResponsiveContentfulImage
                  src={selectedLocation.logo.url}
                  alt={selectedLocation.name ?? "Camp"}
                  className="h-9 w-auto object-contain"
                  sizeMobile="72px"
                  sizeDesktop="72px"
                  lazyLoad={false}
                />
              ) : (
                <Logo className="h-9 text-white" />
              )}
            </Link>

            {locationPages.length > 0 && (
              <Popover>
                {({ open, close }) => (
                  <>
                    <Popover.Button className="flex items-center gap-1 text-white transition-opacity p-2 active:scale-[98%]">
                      {displayLocationName}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                      />
                    </Popover.Button>
                    <LocationSelectPopover
                      locationPages={locationPages}
                      closePopover={close}
                      onSelectLocation={getLocationSelectDestination}
                      selectedLocationName={effectiveLocationName || undefined}
                      isOpen={open}
                      disableDialog
                    />
                  </>
                )}
              </Popover>
            )}
          </div>
          <div className="flex items-center gap-6">
            <Link to="/search" className="xl:hidden" title="Search">
              <Search className="text-white" />
            </Link>
            <button onClick={onClose} title="Close" type="button">
              <Close className="w-4 text-white" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {cmsLocationSlug ? (
            <>
              <HeaderMobileMenuItem
                title="Things To Do"
                icon={<ThingsToDo className="text-white w-6 mt-1" />}
                url={`/${publicLocationSiteSlug}/things-to-do`}
              />
              <HeaderMobileMenuItem
                title="Plan a Party"
                url="/celebrations"
                icon={
                  <Confetti className="w-6 mt-0.5 text-white stroke-none mb-0.5" />
                }
              />
              <HeaderMobileMenuItem
                title="Getting Here"
                url={`/locations/${cmsLocationSlug}`}
                icon={<Location2 className="text-white w-6 mt-1" />}
              />
              <HeaderMobileMenuItem
                title="FAQ"
                url="/help-center"
                icon={<QuestionMark className="stroke-none w-6 mt-0.5" />}
              />
              {featuredActivity && (
                <div className="mx-4 my-4 overflow-hidden">
                  <h3 className="font-serif text-white text-2xl mb-1">
                    See {featuredActivity.name} <br />
                    at {displayLocationName}
                  </h3>
                  {featuredActivity.tagline && (
                    <p className="text-base text-white mb-3 text-pretty">
                      {featuredActivity.tagline}
                    </p>
                  )}
                  {featuredActivity.images?.[6] && (
                    <div className="overflow-hidden rounded-xl relative">
                      <picture className="aspect-[3/2] h-full w-full object-cover">
                        <source
                          srcSet={`${featuredActivity.images?.[0].smallUrl} 500w, ${featuredActivity.images?.[0].largeUrl} 1000w`}
                          sizes="(max-width: 760px) 50w, 100vw"
                        />
                        <img
                          src={featuredActivity.images?.[6].smallUrl as string}
                          loading="lazy"
                          alt={featuredActivity.images?.[6].altText ?? ""}
                          draggable={false}
                        />
                      </picture>
                      <div className="p-4 absolute bottom-0 w-full">
                        {featuredExperiencePath && (
                          <Button
                            onClick={() => navigate(featuredExperiencePath)}
                            className="items-center gap-2 text-camp-primary max-w-86"
                            variant="secondary"
                            size="large"
                            full
                          >
                            {isRedirecting ? (
                              <Spinner className="w-6 h-6 animate-spin" />
                            ) : (
                              <div className="flex w-full justify-between gap-2 items-center">
                                <span className="text-xl">View Details</span>
                                <ArrowRight className="h-6 w-5 stroke-4" />
                              </div>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <HeaderMobileMenuSection
                title="Locations"
                icon={<Location2 className="fill-white w-6 mt-1" />}
                defaultOpen={mobileMenuState == "locations"}
              >
                <HeaderMobileMenuLocations locationPages={locationPages} />
              </HeaderMobileMenuSection>
              {activityLinks.length > 0 && (
                <HeaderMobileMenuSection
                  title="Things To Do"
                  icon={<ThingsToDo className="w-6 mt-1" />}
                  noOpenedBorder
                >
                  <div className="w-full bg-white border-2 border-white">
                    <div className="px-6 pb-6">
                      <HeaderMobilePromos links={activityLinks} />
                    </div>
                  </div>
                </HeaderMobileMenuSection>
              )}
              <HeaderMobileMenuItem
                title="About"
                url="/about-us"
                icon={<HappyFace className="stroke-none w-6 mt-0.5" />}
              />
              <HeaderMobileMenuItem
                title="Plan a Party"
                url="/celebrations"
                icon={
                  <Confetti className="w-6 mt-0.5 text-white stroke-none mb-0.5" />
                }
              />
              <HeaderMobileMenuItem
                title="FAQ"
                url="/help-center"
                icon={<QuestionMark className="stroke-none w-6 mt-0.5" />}
              />
            </>
          )}

          {!selectedLocation && shopPromoLinks && shopPromoLinks.length > 0 ? (
            <div className="p-8 space-y-8 border-t border-b border-white border-opacity-10">
              {shopPromoLinks.map(
                link =>
                  link.image?.url && (
                    <div className="w-full" key={link.sys.id}>
                      <Link to={link.url} title={link.blurb ?? ""}>
                        <ResponsiveContentfulImage
                          src={link.image.url}
                          width={link.image.width ?? undefined}
                          height={link.image.height ?? undefined}
                          alt=""
                          className="rounded-md w-full"
                        />
                      </Link>
                    </div>
                  )
              )}
            </div>
          ) : null}

          <footer className="grid grid-cols-2 gap-4 p-6 border-t border-white border-opacity-10">
            <HeaderMobileMenuColumn
              links={[
                {
                  url: "/products/gift-cards",
                  title: "Gift Cards"
                },
                { url: "/about", title: "About CAMP" },
                { url: "/careers", title: "Careers" }
              ]}
            />

            <HeaderMobileMenuColumn
              links={[
                { url: "/deals", title: "Deals" },
                { url: "/help-center", title: "FAQ" },
                { url: "/contact", title: "Contact Us" },
                {
                  url: "/press-and-partnerships",
                  title: "Press and Partnerships"
                }
              ]}
            />
          </footer>
        </div>
      </Dialog.Panel>
    </Dialog>
  )
}

const HeaderMobilePromos = ({ links }: { links: WithLinkFragment[] }) => (
  <div className="grid grid-cols-2 gap-4 pt-4">
    {links
      .filter(link => !!link.url)
      .map(link => (
        <Link
          key={link.sys.id}
          to={link.url}
          className="flex flex-col justify-end space-y-2"
        >
          <h4 className="font-serif text-lg text-camp-primary">{link.title}</h4>
          <div className="w-full">
            {link.image?.url && (
              <ResponsiveContentfulImage
                src={link.image.url}
                width={link.image.width ?? undefined}
                height={link.image.height ?? undefined}
                sizeMobile="200px"
                sizeDesktop="200px"
                alt=""
                className="rounded-md w-full max-w-[12rem]"
              />
            )}
          </div>
        </Link>
      ))}
  </div>
)

export default HeaderMobileMenuV2
